/**
 * MessageBubble — renders a single chat message (Quiet Trust patient theme).
 *
 * AI messages:      left-aligned, soft teal tint, assistant glyph avatar.
 * Patient messages: right-aligned, solid teal, user glyph avatar.
 * Typing indicator: animated dots while the assistant is responding.
 *
 * Tokens resolve from the surrounding .ci-portal scope. Avatars are decorative
 * SVGs (aria-hidden); the speaker is conveyed via a visually-hidden span so
 * screen readers retain context.
 */

import React from 'react';

interface Props {
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
}

// Decorative avatar glyphs (stroke, currentColor, aria-hidden on the wrapper).
function AssistantGlyph(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    </svg>
  );
}

function UserGlyph(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

export default function MessageBubble({ role, content, timestamp, isTyping }: Props): React.ReactElement {
  const isAI = role === 'assistant';

  return (
    <div
      className={isTyping ? undefined : 'ci-slide-in'}
      style={{ ...styles.row, justifyContent: isAI ? 'flex-start' : 'flex-end' }}
    >
      {isAI && (
        <div style={{ ...styles.avatar, ...styles.avatarAI }} aria-hidden="true">
          <AssistantGlyph />
        </div>
      )}
      <div
        style={{
          ...styles.bubble,
          ...(isAI ? styles.bubbleAI : styles.bubbleUser),
        }}
        title={timestamp.toLocaleTimeString()}
      >
        <span style={styles.srOnly}>{isAI ? 'Assistant said: ' : 'You said: '}</span>
        {isTyping ? (
          <>
            <span style={styles.srOnly} aria-live="polite">Assistant is typing</span>
            <TypingIndicator />
          </>
        ) : (
          <p style={styles.text}>{content}</p>
        )}
        <span style={styles.time}>{formatTime(timestamp)}</span>
      </div>
      {!isAI && (
        <div style={{ ...styles.avatar, ...styles.avatarUser }} aria-hidden="true">
          <UserGlyph />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Typing indicator
// ---------------------------------------------------------------------------

function TypingIndicator(): React.ReactElement {
  return (
    <div style={styles.typing}>
      <span style={{ ...styles.dot, animationDelay: '0ms' }}  />
      <span style={{ ...styles.dot, animationDelay: '160ms' }} />
      <span style={{ ...styles.dot, animationDelay: '320ms' }} />
      <style>{dotAnimation}</style>
    </div>
  );
}

// ci-bounce powers both the typing dots here AND the loading dot in AIIntakeChat,
// so keep it injected. ci-slide-in powers the bubble entrance.
const dotAnimation = `
@keyframes ci-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30%            { transform: translateY(-4px); opacity: 1; }
}
@keyframes ci-slide-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.ci-slide-in { animation: ci-slide-in 0.35s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .ci-slide-in { animation: none !important; }
}
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  row: {
    display:      'flex',
    alignItems:   'flex-end',
    gap:          8,
    marginBottom: 12,
  },
  avatar: {
    width:          22,
    height:         22,
    flexShrink:     0,
    marginBottom:   20,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  avatarAI: {
    borderRadius: 'var(--ci-radius)',
    background:   'var(--ci-primary-light)',
    color:        'var(--ci-primary)',
  },
  avatarUser: {
    borderRadius: '50%',
    background:   'var(--ci-border-light)',
    color:        'var(--ci-text-muted)',
  },
  bubble: {
    maxWidth:     '72%',
    borderRadius: 'var(--ci-radius-lg)',
    padding:      '10px 14px 6px',
    position:     'relative',
  },
  bubbleAI: {
    background:              'var(--ci-primary-light)',
    border:                 '1px solid var(--ci-border-light)',
    color:                  'var(--ci-text-body)',
    borderBottomLeftRadius: 4,
  },
  bubbleUser: {
    background:               'var(--ci-primary)',
    color:                    '#fff',
    borderBottomRightRadius:  4,
  },
  text: {
    margin:       0,
    fontSize:     14,
    lineHeight:   1.6,
    color:        'inherit',
    whiteSpace:   'pre-wrap',
    overflowWrap: 'anywhere',
    fontFamily:   'var(--ci-font)',
  },
  time: {
    display:   'block',
    marginTop: 4,
    fontSize:  10,
    opacity:   0.5,
    textAlign: 'right',
  },
  typing: {
    display:    'flex',
    alignItems: 'center',
    gap:        4,
    padding:    '2px 0 6px',
  },
  dot: {
    display:           'inline-block',
    width:             7,
    height:            7,
    borderRadius:      '50%',
    background:        'var(--ci-primary)',
    animation:         'ci-bounce 1s infinite',
    animationFillMode: 'both',
  },
  srOnly: {
    position:   'absolute',
    width:      1,
    height:     1,
    padding:    0,
    margin:     -1,
    overflow:   'hidden',
    clip:       'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border:     0,
  },
};
